<script setup lang="ts">
import { useSettings } from "~/store/settings";
import Toggle from "./Form/Toggle.vue";
import t from "~/utils/translate";
import Input from "./Form/Input.vue";
import Button from "./Button.vue";
import Dropdown from "./Form/Dropdown.vue";

interface Props {
	open: boolean;
}

defineProps<Props>();
const store = useSettings();

const search = ref<string>("");
const sort = ref<any>("default");
const sortOptions = ref<{ name: string; value: string }[]>([
	{ name: "Default", value: "default" },
	{ name: "Alphabetical", value: "alphabetical" },
	{ name: "On/Off", value: "toggle" },
]);

const featureFlags = computed(() => {
	return {
		featureFlags: Object.fromEntries(
			Object.entries(store.config?.appSetting?.FeatureFlagsList || {}).filter(([_, value]) => typeof value === "boolean")
		) as Record<string, boolean>,
		jackpotSettings: Object.fromEntries(
			Object.entries(store.config?.appSetting?.JackpotSettings || {}).filter(([_, value]) => typeof value === "boolean")
		) as Record<string, boolean>,
		ResponsibleGamingSwitches: Object.fromEntries(
			Object.entries(store.config?.appSetting?.ResponsibleGamingSwitches || {}).filter(([_, value]) => typeof value === "boolean")
		) as Record<string, boolean>,
	};
});

const toggleFeatureFlag = (name: string, value: boolean, index: string) => {
	if (store.config?.appSetting) {
		if (index === "featureFlags") {
			store.config.appSetting.FeatureFlagsList[name] = value;
		}

		if (index === "jackpotSettings") {
			store.config.appSetting.JackpotSettings = {
				...store.config.appSetting.JackpotSettings,
				[name]: value,
			};
		}

		if (index === "ResponsibleGamingSwitches") {
			store.config.appSetting.ResponsibleGamingSwitches = {
				...store.config.appSetting.ResponsibleGamingSwitches,
				[name]: value,
			};
		}
	}
};

const sortFeatureFlags = (flags: [string, boolean][]) => {
	switch (sort.value) {
		case "default":
			return flags;
		case "alphabetical":
			return flags.sort((a, b) => a[0].localeCompare(b[0]));
		case "toggle":
			flags.sort((x, y) => {
				return x[1] === y[1] ? 0 : x[1] ? -1 : 1;
			});
		default:
			return flags;
	}
};
</script>

<template>
	<div
		class="fixed transition-all duration-300 ease-in-out text-light-50 bg-dark-800 w-screen h-fit z-[60] pt-16 p-5"
		:class="open ? 'bottom-[138px] md:bottom-[60px]' : '-bottom-full'"
	>
		<div class="md:flex !z-[90] absolute top-0 mt-3 items-center gap-3 bg-dark-800 text-light-50 mb-20 dark hidden">
			<Dropdown :options="sortOptions" v-model="sort" label="Sort" inputClass="bg-[transparent]" class="w-52" />
			<Input type="text" :modelValue="search" @update:modelValue="(val) => (search = val)" label="Search" clearInputIcon class="w-52" />
		</div>

		<!-- Large Screens -->
		<div class="md:grid hidden grid-cols-3">
			<div v-for="(flagObject, index) in featureFlags" class="overflow-y-auto overflow-x-hidden h-[600px] px-3 flex flex-col scrollbar-hidden">
				<label class="sticky top-0 -translate-y-1 text-center bg-dark-800 z-20 text-lg pb-3 pt-3">{{ t(index) }}</label>
				<div v-for="featureFlag in sortFeatureFlags(Object.entries(flagObject || {})).filter((x) => x[0].toLowerCase().includes(search.toLowerCase()))">
					<Toggle
						:size="'medium'"
						:label="featureFlag[0]"
						:modelValue="featureFlag[1]"
						@update:modelValue="toggleFeatureFlag(featureFlag[0], !featureFlag[1], index)"
					/>
				</div>
				<div
					v-if="sortFeatureFlags(Object.entries(flagObject || {})).filter((x) => x[0].toLowerCase().includes(search)).length <= 0"
					class="w-full text-center text-primary-600"
				>
					No {{ t(index) }} Found!
				</div>
			</div>
		</div>
	</div>

	<!-- Small Screens -->
	<div
		class="fixed transition-all duration-300 ease-in-out text-light-50 z-40 bg-dark-800 w-screen h-[70%] md:hidden flex flex-col px-5 pb-20 overflow-auto"
		:class="open ? 'bottom-[138px] md:bottom-[62px]' : '-bottom-full'"
	>
		<div class="flex flex-wrap !z-[90] justify-evenly mt-3 items-center bg-dark-800 text-light-50 dark pt-1">
			<Dropdown :options="sortOptions" v-model="sort" label="Sort" inputClass="bg-[transparent]" class="w-[48%]" />
			<Input type="text" :modelValue="search" @update:modelValue="(val) => (search = val)" label="Search" clearInputIcon class="w-[48%]" />
		</div>

		<template v-for="(flagObject, index) in featureFlags">
			<label class="sticky top-0 -translate-y-1 text-center bg-dark-800 z-20 text-lg py-3">{{ t(index) }}</label>
			<div v-for="featureFlag in sortFeatureFlags(Object.entries(flagObject || {})).filter((x) => x[0].toLowerCase().includes(search.toLowerCase()))">
				<Toggle
					:label="featureFlag[0]"
					:modelValue="featureFlag[1]"
					:size="'medium'"
					@update:modelValue="toggleFeatureFlag(featureFlag[0], !featureFlag[1], index)"
				/>
			</div>

			<div
				v-if="sortFeatureFlags(Object.entries(flagObject || {})).filter((x) => x[0].toLowerCase().includes(search.toLowerCase())).length <= 0"
				class="w-full text-center text-primary-600"
			>
				No {{ t(index) }} Found!
			</div>
		</template>
	</div>
</template>
